import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription, from } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { ProductStatus } from '@app/core/_constant/product.constants';
import { DefaultShipToAddress } from '@app/core/_constant/purchase-order.constant';
import { LocationService } from '@app/core/locations/services/location.service';
import { ProductTagService } from '@app/core/product-tags/services/product-tag.service';
import { ReceivingReportService } from '@app/core/purchase-orders/services/receiving-report.service';
import { RFQ } from '@app/core/quotes/store/quotes.model';
import { RfqLotService } from '@app/core/rfq-lots/services/rfq-lots.service';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { environment } from '@app/env-service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { IRfqLot } from 'v2/modules/rfq/interfaces/rfq-lot.interface';
import { IWarehouse } from 'v2/modules/shared/interfaces/location-warehouse.interface';
import { DecimalPlacesPipe } from 'v2/modules/shared/pipes/decimal-places.pipe';

import { ReceivingReportFormRfqsComponent } from '../receiving-report-form-rfqs/receiving-report-form-rfqs.component';
import { RfqLotFormComponent } from '../rfq-lot-form/rfq-lot-form.component';

@Component({
  selector: 'cnc-receiving-report-form',
  templateUrl: './receiving-report-form.component.html',
  styleUrls: ['./receiving-report-form.component.scss'],
})
export class ReceivingReportFormComponent implements OnInit, OnDestroy {
  url :string = environment.url;
  datePipe = new DatePipe('en');

  loading :boolean = false;
  pageTitle :string = 'Receiving Report Form';

  formData :UntypedFormGroup;
  totalUnits :number = 0;
  totalCost :number = 0;

  warehouses :any[] = [];
  locations :any[] = [];
  tagsData :any[] = [];
  tags :any[] = [];
  unsubscribeTags :Subscription;
  rfqLotsSubscription :Subscription;

  locations$ :Observable<any>;

  productStatus = ProductStatus;

  private onDestroy$ = new Subject();

  unitOfMeasures :any[] = [];
  rfqs :RFQ[] = [];

  @Input() receivingReport;
  inventorySettings :any;
  settingsSubscription :Subscription;

  rfqLots :any[] = [];
  rfqLots$ :Observable<any>;
  ftLocationBins :boolean = false;
  ftLotTracking :boolean = false;

  @Input()
  lineItemsFromPO :any[] = [];
  featureFlag = EFeatureFlag;

  @Input()
  totalShippingCost :number = 0;

  constructor(
    public activeModal :NgbActiveModal,
    private modalSvc :NgbModal,
    private fb :UntypedFormBuilder,
    private toastr :ToastrService,
    public rrSvc :ReceivingReportService,
    private cd :ChangeDetectorRef,
    public locationSvc :LocationService,
    private translate :TranslateService,
    private tagSvc :ProductTagService,
    public rfqLotSvc :RfqLotService,
    private settingsSvc :SettingsService,
    private ft :FeatureToggleService,
    private decimalPlaces :DecimalPlacesPipe,
  ) {
    this.ftLotTracking = this.ft.featureEnabled(EFeatureFlag.LOT_TRACKING);
  }

  ngOnInit() {
    this.initTrackInventoryByLocation();
    this.initInventorySettings();
    // this.loadLocationsRelatedFunctions();
    // this.getLocations();
    this.loadTags();

    this.formData = this.fb.group({
      received_date: [DateTime.now().toISODate(), Validators.required],
      received_by: [null],
      received_at: [null],
      tracking_number: [null],
      shipped_via: [''],
      total_quantity: [null],
      total_tax: [null],
      total_cost: [null],
      items: this.fb.array([]),
      shipping_cost: [0],
      bulkWarehouseId: [0],
      bulkLocationId: [0],
      bulkWarehouse: [null],
      bulkLocation: [null],
    });
    this.formData.get('bulkLocationId').disable();
    this.formData.get('bulkWarehouseId').disable();

    this.formData.get('bulkWarehouse').disable();
    this.formData.get('bulkLocation').disable();

    if (this.receivingReport) {
      const receivedDate = this.receivingReport.received_date;
      this.formData.patchValue({
        received_date: receivedDate.slice(0, receivedDate.indexOf(' ')),
        received_by: this.receivingReport.received_by,
        received_at: this.receivingReport.received_at,
        tracking_number: this.receivingReport.tracking_number,
        shipped_via: this.receivingReport.tracking_number,
        shipping_cost: this.receivingReport.shipping_cost,
      });
      // load items
      // this.fetchRfqLots(this.receivingReport.receiving_report_rfq);

      this.receivingReport.receiving_report_rfq.forEach((rrRfq) => {
        if (rrRfq.purchase_order_rfq && rrRfq.purchase_order_rfq.rfq) {
          this.unitOfMeasures[rrRfq.purchase_order_rfq.rfq.id] =
            rrRfq.purchase_order_rfq.rfq.unit_of_measures;
          this.rfqs[rrRfq.purchase_order_rfq.rfq.id] = rrRfq.purchase_order_rfq.rfq;
        }

        this.addItem({
          id: rrRfq.id,
          purchase_order_id: rrRfq?.purchase_order_rfq?.purchase_order_id,
          purchase_order_rfq_id: rrRfq.purchase_order_rfq_id,
          quantity: rrRfq.quantity,
          internal_sku: rrRfq.purchase_order_rfq?.internal_sku,
          product_name: rrRfq.purchase_order_rfq?.product_name,
          unit: rrRfq.unit,
          unit_price: rrRfq.unit_price,
          line_total: rrRfq.line_total,
          warehouse_id: rrRfq.stock ? rrRfq.stock.warehouse_id : 0,
          location_id: rrRfq.location_id ? rrRfq.location_id : 0,
          tag_ids: rrRfq?.tags ? rrRfq.tags.map((data) => data.id) : null,
          rfq_id: rrRfq.purchase_order_rfq?.rfq_id,
          received_uom_id: rrRfq.received_uom_id,
          lot_id: rrRfq.lot_id ? rrRfq.lot_id : 0,
          lot_tracking: rrRfq.purchase_order_rfq?.rfq.lot_tracking
            ? rrRfq.purchase_order_rfq?.rfq.lot_tracking
            : false,
          lot: rrRfq.rfq_lot,
          warehouse: rrRfq.stock?.warehouse || null,
          location: rrRfq.location || null,
        });

        if (rrRfq?.stock?.location) {
          this.locations = [...this.locations, rrRfq.stock.location];
        }
      });
      this.totalCost = this.receivingReport.total_cost;
      this.totalUnits = this.receivingReport.total_quantity;
    } else if (this.lineItemsFromPO?.length > 0) {
      const rfqIds = this.lineItemsFromPO?.map((item) => item.rfqId);
      // this.getRfqLots(rfqIds);

      const warehouseIds = [];
      const lineItemIndex = [];
      this.lineItemsFromPO.forEach((item, index) => {
        this.unitOfMeasures[item.rfq.id] = item.rfq.unitOfMeasures;
        this.rfqs[item.rfq.id] = item.rfq;

        const pickingWarehouseId = this.inventorySettings
          ? this.inventorySettings.warehouse_id
          : 0;

        if (this.itemLocationEnabled) {
          if (
            item.purchaseOrder?.defaultShipToAddress !==
              DefaultShipToAddress.WAREHOUSE &&
            item?.rfq?.defaultReceivingBin
          ) {
            item.warehouseId = item.rfq.defaultReceivingBin.parentId;
            item.locationId = item.rfq.defaultReceivingBin.id;
          }
          if (pickingWarehouseId > 0) {
            item.warehouseId = pickingWarehouseId;
            //set item.locationId if item?.rfq and use the item?.location?.id
            if (!item?.locationId) {
              item.locationId = item?.location?.id;
            }
          }
          if (
            item.purchaseOrder?.defaultShipToAddress ===
              DefaultShipToAddress.WAREHOUSE &&
            item.purchaseOrder?.warehouseId !== 0
          ) {
            item.warehouseId = item.purchaseOrder?.warehouseId;
            item.locationId = item?.location?.id || null;
            if (
              pickingWarehouseId === item?.rfq?.defaultReceivingBin?.parentId &&
              item.purchaseOrder?.warehouseId ===
                item?.rfq?.defaultReceivingBin?.parentId
            ) {
              item.locationId = item?.rfq?.defaultReceivingBin?.id;
            }
          }
        }
        this.formData.patchValue({
          shipping_cost: this.totalShippingCost,
        });

        this.addItem({
          purchase_order_id: item.purchaseOrderId,
          purchase_order_rfq_id: item.id,
          quantity: item.addToReceiveQty,
          internal_sku: item.internalSku,
          product_name: item.productName,
          unit: item.unit,
          unit_price: item.unitPrice,
          line_total: item.unitPrice * item.addToReceiveQty,
          warehouse_id: item.destinationWarehouseId ?? item.warehouseId,
          location_id: item.destinationWarehouseId
            ? item?.location?.id
            : item.locationId,
          tag_ids: null,
          rfq_id: item.rfq?.id,
          received_uom_id: item.orderedUomId,
          lot_id: item.lotId,
          lot_tracking: item.rfq?.lotTracking ? item.rfq?.lotTracking : false,
          lot: item.lot,
          warehouse: item?.warehouse || null,
          location: item?.location || null,
        });

        if (!warehouseIds.includes(item.warehouseId)) {
          warehouseIds.push(item.warehouseId);
          lineItemIndex.push(index);
        }
      });
      lineItemIndex.forEach((itemIndex) => {
        this.loadWarehouseLocations(itemIndex);
      });
      this.computeTotal();
    }
    this.setBulkUpdateWarehouseValue();
    this.setBulkUpdateLocationValue();
  }

  ngOnDestroy() :void {
    if (this.unsubscribeTags) {
      this.unsubscribeTags.unsubscribe();
    }
    if (this.rfqLotsSubscription) {
      this.rfqLotsSubscription.unsubscribe();
    }
    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
    }

    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  getLocations() {
    this.locationSvc
      .getAll({
        all: 1,
        parent_id: 0,
        withoutStockLocations: true,
      })
      .subscribe((resp) => {
        this.warehouses = resp.filter((row) => row.parent_id == 0);
      });
  }

  save() {
    if (this.formData.invalid) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
        text: this.translate.instant('MESSAGES.FORM_ERROR.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }

    const payload :any = this.formData.value;

    if (payload.items.length == 0) {
      Swal.fire({
        title: 'Items/SKUs are required.',
        text: 'Please add at least one(1) item/rfq received.',
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }

    const errors = {
      location: [],
      warehouse: [],
      quantity: [],
    };
    payload.items.forEach((item) => {
      if (
        (item.location_id <= 0 || isNaN(item.location_id)) &&
        this.itemLocationEnabled
      ) {
        errors.location.push(item.sku);
      }
      if (item.warehouse_id <= 0) {
        errors.warehouse.push(item.sku);
      }
      if (item.quantity <= 0) {
        errors.quantity.push(item.sku);
      }
    });

    if (errors.location.length > 0) {
      const skus = errors.location.join(', ');
      Swal.fire({
        title: 'Please set the location for the following SKUs:',
        text: skus,
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }

    if (errors.warehouse.length > 0) {
      const skus = errors.warehouse.join(', ');
      Swal.fire({
        title: 'Please set the warehouse for the following SKUs:',
        text: skus,
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }

    if (errors.quantity.length > 0) {
      const skus = errors.quantity.join(', ');
      Swal.fire({
        title: 'Please set the quantity for the following SKUs:',
        text: skus,
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }
    let payloadTotalCost = this.totalCost;
    let payloadTotalUnit = this.totalUnits;
    payload.items.forEach((row :any) => {
      if (!row?.rfq_id) {
        payloadTotalCost -= row.line_total;
        payloadTotalUnit -= row.quantity;
      }
      if (row?.location?.rfqstock_locations) {
        delete row.location.rfqstock_locations;
      }
    });
    payload.total_cost = payloadTotalCost;
    payload.total_quantity = payloadTotalUnit.toString();
    payload.total_tax = 0;
    payload.updatedItems = this.formData.controls.items.dirty;
    payload.items = payload.items.map((item) => ({
      ...item,
      quantity: item.quantity.toString(),
    }));
    payload.shipping_cost = this.totalShippingCost;

    delete payload.bulkWarehouseId;
    delete payload.bulkLocationId;

    this.loading = true;

    if (this.receivingReport) {
      this.rrSvc.updateReceivingReport(this.receivingReport.id, payload).subscribe(
        (resp) => {
          this.toastr.success('Receiving report saved.');
          this.loading = false;
          this.activeModal.close(true);
        },
        (error) => {
          this.loading = false;
          this.toastr.error(
            'Error saving receiving report. Please try again later or contact support.',
          );
        },
      );
    } else {
      this.rrSvc.createReceivingReport(payload).subscribe(
        (resp) => {
          this.toastr.success('Receiving report saved.');
          this.loading = false;
          this.activeModal.close(true);
        },
        (error) => {
          this.loading = false;
          this.toastr.error(
            'Error saving receiving report. Please try again later or contact support.',
          );
        },
      );
    }
  }

  computeLineTotal(idx) {
    const items :UntypedFormArray = this.formData.get('items') as UntypedFormArray;
    const lineItem :UntypedFormGroup = items.at(idx) as UntypedFormGroup;
    const lineTotal :number = lineItem.value.quantity * lineItem.value.unit_price;

    lineItem.get('line_total').patchValue(this.decimalPlaces.transform(lineTotal));
    this.computeTotal();
  }

  computeTotal() {
    const items :UntypedFormArray = this.formData.get('items') as UntypedFormArray;
    this.totalUnits = 0;
    this.totalCost = 0;

    items.value.forEach((row) => {
      this.totalUnits += Number(row.quantity);
      this.totalCost += Number(row.line_total);
    });
  }

  addItem(item :any) {
    const items :UntypedFormArray = this.formData.get('items') as UntypedFormArray;
    const itemGroup :UntypedFormGroup = this.fb.group({
      purchase_order_id: [item?.purchase_order_id],
      purchase_order_rfq_id: [item.purchase_order_rfq_id],
      quantity: [item.quantity],
      sku: [item.internal_sku],
      item: [item.product_name],
      unit: [item.unit],
      unit_price: [item.unit_price],
      line_total: [item.line_total],
      warehouse_id: [item.warehouse_id],
      location_id: [item.location_id],
      id: [item.id ? item.id : 0],
      tags: [item.tag_ids],
      received_uom_id: [item.received_uom_id],
      rfq_id: [item.rfq_id],
      lot_id: [item.lot_id],
      lot_tracking: [item.lot_tracking],
      original_lot_id: [item.lot_id],
      lot: [item.lot],
      warehouse: [item.warehouse],
      location: [item.location],
    });

    items.push(itemGroup);
    this.toggleBulkWarehouseField();
  }

  customTagsSearchFn(term :any, item :any) :void {
    term = term.toLocaleLowerCase();

    return item.name
      .toLowerCase()
      .replace(/&nbsp;/gi, '')
      .includes(term);
  }

  private loadTags() :void {
    this.tagSvc.getTags({ parent_only: true }, null);
    this.unsubscribeTags = this.tagSvc.formattedRecord$.subscribe((tags :any) => {
      this.tags = tags;
    });
  }

  duplicateRow(item, idx) {
    const items :UntypedFormArray = this.formData.get('items') as UntypedFormArray;
    items.insert(idx, item);
  }

  addPOItems() {
    const modalRef = this.modalSvc.open(ReceivingReportFormRfqsComponent, {
      size: 'lg',
      windowClass: 'po-rr-modal',
    });

    modalRef.componentInstance.itemLocationsEnabled = this.itemLocationEnabled;
    modalRef.componentInstance.ftLocationBins = this.ftLocationBins;
    modalRef.componentInstance.ftLotTracking = this.ftLotTracking;
    modalRef.componentInstance.pickingWarehouseId = this.inventorySettings
      ? this.inventorySettings.warehouse_id
      : 0;
    modalRef.componentInstance.warehouses = this.warehouses;

    modalRef.result.then(
      (data) => {
        const items = data.rrItems;

        data.unitOfMeasures.forEach((uom, rfqId) => {
          this.unitOfMeasures[rfqId] = uom;
        });

        if (items && items.length > 0) {
          items.forEach((item) => {
            this.rfqs[item.rfq.id] = item.rfq;
            this.addItem(item);
          });
          this.computeTotal();

          this.setBulkUpdateWarehouseValue();
          this.setBulkUpdateLocationValue();
        }
      },
      (_) => {},
    );
  }

  onWarehouseChange(idx :number) :void {
    this.formArrayItems.at(idx).patchValue({ location_id: null });
    this.setBulkUpdateWarehouseValue();
    this.setBulkUpdateLocationValue();
  }

  onWarehouseFormSelect(warehouseId :number) :void {
    if (typeof warehouseId === 'string') {
      warehouseId = parseInt(warehouseId);
    }

    this.formArrayItems.controls.forEach((item) => {
      item.patchValue({
        warehouse_id: warehouseId,
        location_id: 0,
      });
    });

    this.setBulkUpdateWarehouseValue();
    this.setBulkUpdateLocationValue();

    const hasLocations = this.locations.some(
      (location) => location.parent_id === warehouseId,
    );
    if (!hasLocations) {
      this.locationSvc
        .getLocations(warehouseId, true, true)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe();
    }
  }

  onLocationFormSelect(locationId :number) :void {
    if (typeof locationId === 'string') {
      locationId = parseInt(locationId);
    }

    if (this.formData.value.bulkWarehouseId > 0) {
      this.formArrayItems.controls.forEach((item) => {
        item.patchValue({
          location_id: locationId,
        });
      });
      this.setBulkUpdateLocationValue();
    }
  }

  loadWarehouseLocations(idx :number) :void {
    const item = this.formArrayItems.at(idx);
    const warehouseId = item?.value.warehouse_id;

    if (!warehouseId) return;

    this.locationSvc
      .getLocations(warehouseId, true)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  get formArrayItems() :UntypedFormArray {
    const items :UntypedFormArray = this.formData.get('items') as UntypedFormArray;

    return items;
  }

  get itemLocationEnabled() :boolean {
    return this.ftLocationBins;
  }

  private loadDataFromService() :void {
    this.locations$ = this.locationSvc.locationsRecord$;
    this.locations$.subscribe((locations) => {
      this.locations = locations;
    });

    this.rfqLots$ = this.rfqLotSvc.rfqLots$;
    this.rfqLotsSubscription = this.rfqLots$.subscribe((rfqLots) => {
      const tempRfqLots = [];

      rfqLots.forEach((rfqLotData :any) => {
        const exists =
          this.rfqLots.findIndex((rfqLot) => rfqLot.id === rfqLotData.id) > -1;

        if (!exists) {
          tempRfqLots.push(rfqLotData);
        }
      });

      this.rfqLots = this.rfqLots.concat(tempRfqLots);
    });
  }

  private loadSelectedLocations() :void {
    if (this.receivingReport) {
      const selectedWarehouseIds = this.receivingReport.receiving_report_rfq.reduce(
        (ids, rfq) => {
          if (rfq?.stock?.warehouse_id && !ids.includes(rfq.stock.warehouse_id)) {
            return [...ids, rfq.stock.warehouse_id];
          }

          return ids;
        },
        [],
      );

      selectedWarehouseIds.forEach((id) => {
        this.locationSvc.getLocations(id).subscribe();
      });
    }
  }

  getUomName(rfq_id :any, received_uom_id :any) :any {
    const result = Array.isArray(this.unitOfMeasures[rfq_id]);

    if (result) {
      return this.unitOfMeasures[rfq_id].find((x) => x.id === received_uom_id)
        ?.name;
    } else {
      return '';
    }
  }

  private loadLocationsRelatedFunctions() :boolean {
    if (!this.itemLocationEnabled) return;

    this.loadDataFromService();
    this.loadSelectedLocations();
  }

  private fetchRfqLots(rrRfq :any = []) :void {
    if (rrRfq?.length > 0) {
      const rfqIds = rrRfq.map((item) => item.purchase_order_rfq?.rfq_id);
      this.getRfqLots(rfqIds);
    }
  }

  private checkIfRfqLotExists(rfqIds :number[]) :boolean {
    return !this.rfqLots.some((rfqLot) =>
      rfqIds.some((rfqId) => rfqId === rfqLot.rfq_id),
    );
  }

  getRfqLots(rfqIds :number[]) :void {
    this.rfqLotSvc
      .getByRfqIds({
        rfqIds: rfqIds.join(','),
        limit: 1000,
      })
      .subscribe((resp) => {
        if (resp.data) {
          const tempRfqLots = [];

          resp.data.forEach((rfqLotData :any) => {
            const exists =
              this.rfqLots.findIndex((rfqLot) => rfqLot.id === rfqLotData.id) > -1;

            if (!exists) {
              tempRfqLots.push(rfqLotData);
            }
          });

          this.rfqLots = this.rfqLots.concat(tempRfqLots);
        }
      });
  }

  enabledBinLocationAndLotTracking(item :any) :boolean {
    return item.lot_tracking && this.ftLocationBins && this.ftLotTracking;
  }

  initInventorySettings() :void {
    this.settingsSubscription = this.settingsSvc.inventorySettings$.subscribe(
      (resp :any) => {
        if (resp && resp.id) {
          this.inventorySettings = JSON.parse(resp.value);
          this.inventorySettings.warehouse_id = parseInt(
            this.inventorySettings.warehouse_id,
          );
        } else {
          this.inventorySettings = null;
        }
      },
    );
  }

  initTrackInventoryByLocation() :void {
    this.settingsSubscription =
      this.settingsSvc.trackInventoryByLocationValue$.subscribe(
        (enabled :boolean) => {
          this.ftLocationBins = enabled;
        },
      );
  }

  removeItem(index :number) :void {
    this.formArrayItems.removeAt(index);
    this.computeTotal();
    this.toggleBulkWarehouseField();
  }

  private setBulkUpdateWarehouseValue() :void {
    const items :UntypedFormArray = this.formData.get('items') as UntypedFormArray;
    if (items?.length > 0) {
      const warehouseIds = items?.controls.map((item) => item.value.warehouse_id);
      const allEqual = warehouseIds.every(
        (warehouseId) => warehouseId === warehouseIds[0],
      );

      this.formData.patchValue({
        bulkWarehouseId: allEqual ? warehouseIds[0] : null,
        bulkWarehouse: allEqual ? items.at(0).value.warehouse : null,
      });
    }
    this.toggleBulkLocationField();
  }

  private setBulkUpdateLocationValue() :void {
    const items :UntypedFormArray = this.formData.get('items') as UntypedFormArray;
    if (items?.length > 0) {
      const locationIds = items?.controls.map((item) => item.value.location_id);
      const allEqual = locationIds.every(
        (locationId) => locationId === locationIds[0],
      );

      this.formData.patchValue({
        bulkLocationId: allEqual ? locationIds[0] : null,
        bulkLocation: allEqual ? items.at(0).value.location : null,
      });
    }
  }

  private toggleBulkWarehouseField() :void {
    if (this.formArrayItems.length <= 0) {
      this.formData.get('bulkWarehouseId').disable();
      this.formData.get('bulkWarehouse').disable();

      return;
    }

    this.formData.get('bulkWarehouseId').enable();
    this.formData.get('bulkWarehouse').enable();
  }

  private toggleBulkLocationField() :void {
    if (
      !this.formData.value.bulkWarehouseId ||
      this.formData.value.bulkWarehouseId <= 0
    ) {
      this.formData.get('bulkLocationId').disable();
      this.formData.get('bulkLocation').disable();

      return;
    }

    this.formData.get('bulkLocationId').enable();
    this.formData.get('bulkLocation').enable();
  }

  onLotChange(event :any, idx :number = 0) :void {
    if (event.target.value === 'createNewLot') {
      const lineItem = this.formArrayItems.at(idx);
      lineItem.get('lot_id').patchValue(lineItem.value.original_lot_id);
      this.createNewLot(lineItem);
    }
  }

  createNewLot(lineItem :AbstractControl) :void {
    const modalRef = this.modalSvc.open(RfqLotFormComponent, {
      size: 'lg',
      windowClass: 'po-rr-modal',
    });
    modalRef.componentInstance.rfqId = lineItem.value.rfq_id;
    modalRef.componentInstance.quantityOriginal = 0;
    modalRef.componentInstance.purchaseOrderId = lineItem.value.purchase_order_id;

    modalRef.result.then(
      (data) => {
        lineItem.get('lot_id').patchValue(data.id);
      },
      (_) => {},
    );
  }

  onSelectLot(selectedRfqLot :IRfqLot, idx :number) :void {
    const lineItem = this.formArrayItems.at(idx);
    lineItem.patchValue({
      lot_id: selectedRfqLot.id,
      lot: selectedRfqLot,
    });
  }

  onOpenCreateLotDialog(lineItem :any, idx :number) :void {
    const modalRef = this.modalSvc.open(RfqLotFormComponent, {
      size: 'lg',
      windowClass: 'po-rr-modal',
    });
    modalRef.componentInstance.rfqId = lineItem.value.rfq_id;
    modalRef.componentInstance.quantityOriginal = 0;
    modalRef.componentInstance.purchaseOrderId = lineItem.value.purchase_order_id;

    from(modalRef.result)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedRfqLot) => {
        lineItem.patchValue({
          lot_id: selectedRfqLot.id,
          lot: selectedRfqLot,
        });
      });
  }

  onSelectWarehouse(selectedWarehouse :IWarehouse, idx :number) :void {
    const lineItem = this.formArrayItems.at(idx);
    lineItem.patchValue({
      warehouse_id: selectedWarehouse.id,
      warehouse: selectedWarehouse,
      location_id: null,
    });

    this.setBulkUpdateWarehouseValue();
    this.setBulkUpdateLocationValue();
  }

  onSelectLocation(selectedLocation :IWarehouse, idx :number) :void {
    const lineItem = this.formArrayItems.at(idx);
    lineItem.patchValue({
      location_id: selectedLocation.id,
      location: selectedLocation,
    });

    this.setBulkUpdateLocationValue();
  }

  onSelectBulkUpdateWarehouse(warehouse :IWarehouse) :void {
    this.formData.patchValue({
      bulkWarehouse: warehouse,
      bulkWarehouseId: warehouse.id,
      bulkLocation: null,
      bulkLocationId: 0,
    });

    this.formArrayItems.controls.forEach((item) => {
      item.patchValue({
        warehouse_id: warehouse.id,
        warehouse: warehouse,
        location_id: 0,
        location: null,
      });
    });

    this.setBulkUpdateWarehouseValue();
    this.setBulkUpdateLocationValue();
  }

  onSelectBulkUpdateLocation(location :IWarehouse) :void {
    this.formData.patchValue({
      bulkLocation: location,
      bulkLocationId: location.id,
    });

    this.formArrayItems.controls.forEach((item) => {
      item.patchValue({
        location_id: location.id,
        location: location,
      });
    });
    this.setBulkUpdateLocationValue();
  }

  get itemsArray() :UntypedFormArray {
    return this.formData.get('items') as UntypedFormArray;
  }
}
